import React from 'react'
import { Card as MuiCard, CardContent, CardActions, type CardProps as MuiCardProps } from '@mui/material'
import { styled } from '@mui/material/styles'
import { colors, spacing, typography } from '../../../tokens'

export interface CardProps extends Omit<MuiCardProps, 'variant'> {
  variant?: 'default' | 'workspace' | 'template' | 'stat' | 'elevated' | 'highlight'
  padding?: 'none' | 'small' | 'medium' | 'large'
  hoverable?: boolean
  children: React.ReactNode
  actions?: React.ReactNode
}

const StyledCard = styled(MuiCard)<{
  customVariant: CardProps['variant']
  customPadding: CardProps['padding']
  hoverable: boolean
}>(({ customVariant, customPadding, hoverable }) => {
  const getVariantStyles = () => {
    switch (customVariant) {
      case 'workspace':
        return {
          background: colors.background.card,
          position: 'relative' as const,
          overflow: 'hidden' as const,
        }
      case 'template':
        return {
          background: colors.background.card,
          border: `1px solid ${colors.border.primary}`,
          '&:hover': hoverable ? {
            borderColor: colors.accent.main,
            transform: 'translateY(-2px)',
          } : {},
        }
      case 'stat':
        return {
          background: colors.background.card,
          textAlign: 'center' as const,
          '& .stat-number': {
            fontSize: typography.scale.statNumber.fontSize,
            fontWeight: typography.scale.statNumber.fontWeight,
            lineHeight: typography.scale.statNumber.lineHeight,
            color: colors.accent.main,
            marginBottom: spacing.scale[2],
          },
          '& .stat-label': {
            fontSize: typography.scale.caption.fontSize,
            fontWeight: typography.fontWeight.medium,
            color: colors.text.secondary,
            textTransform: 'uppercase' as const,
            letterSpacing: '0.04em',
          },
        }
      case 'elevated':
        return {
          background: colors.background.card,
          boxShadow: spacing.shadow.lg,
          '&:hover': hoverable ? {
            boxShadow: spacing.shadow.xl,
            transform: 'translateY(-4px)',
          } : {},
        }
      case 'highlight':
        return {
          background: colors.background.highlight,
          border: `1px solid ${colors.accent.lighter}`,
        }
      case 'default':
      default:
        return {
          background: colors.background.card,
        }
    }
  }

  const getPaddingStyles = () => {
    switch (customPadding) {
      case 'none': return { padding: 0 }
      case 'small': return { padding: spacing.scale[4] }
      case 'large': return { padding: spacing.scale[8] }
      case 'medium':
      default: return { padding: spacing.component.cardPadding }
    }
  }

  return {
    borderRadius: spacing.borderRadius.card,
    border: `1px solid ${colors.border.primary}`,
    boxShadow: spacing.shadow.card,
    transition: `all ${spacing.transitions.fast} ${spacing.transitions.easing}`,
    cursor: hoverable ? 'pointer' : 'default',
    '&:hover': hoverable ? {
      boxShadow: spacing.shadow.cardHover,
      transform: 'translateY(-2px)',
    } : {},
    ...getVariantStyles(),
    '& .MuiCardContent-root': {
      ...getPaddingStyles(),
      '&:last-child': {
        paddingBottom: getPaddingStyles().padding,
      },
    },
  }
})

export const Card: React.FC<CardProps> = ({
  variant = 'default',
  padding = 'medium',
  hoverable = false,
  children,
  actions,
  ...props
}) => {
  return (
    <StyledCard
      customVariant={variant}
      customPadding={padding}
      hoverable={hoverable}
      {...props}
    >
      <CardContent>
        {children}
      </CardContent>
      {actions && (
        <CardActions sx={{ padding: spacing.scale[4], paddingTop: 0 }}>
          {actions}
        </CardActions>
      )}
    </StyledCard>
  )
}

export default Card
